import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin } from 'rxjs';

import { AuthService } from '../../../services/auth/auth.service';
import { AppService } from '../../../core/app.service';
import { ActiviteService } from '../../../services/activite/activite.service';
import { StructureProgammatiqueService } from '../../../services/structureProgrammatiquesService';
import { TypeInterventionService } from '../../../services/typeintervention/typeintervention.service';
import { KeycloakAdminService } from '../../../services/keycloak/adminservice/KeycloakAdminService';

import { ActivtieResponse } from '../../../data/http/responses/activite.response';
import { StructureProgrammatiqueResponse } from '../../../data/http/responses/structureprogrammatique.res';
import { TypeInterventionResponse } from '../../../data/http/responses/typeintervention.response';

import { UiTable, Column } from '../../../components/ui/table/table.component';

// ─── Types internes au dashboard ───────────────────────────────────────────
interface Kpi {
  title: string;
  value: string;
  icon: string;
  tone: 'primary' | 'success' | 'warning' | 'danger';
}

interface BudgetCategory {
  name: string;
  spent: number;
  allocated: number;
}

interface ProjetActif {
  id: string;
  name: string;
  dateDebut: string | null;
  dateFin: string | null;
  progress: number;
  sousInterventionsCount: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, MatIconModule, UiTable],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
})
export class DashboardComponent implements OnInit {
  public username: string | undefined;

  // ─── États de chargement / erreur, par source ──────────────────────────
  public isLoadingStructures = true;
  public isLoadingActivites = true;
  public isLoadingUsers = true;

  public errorStructures: string | null = null;
  public errorActivites: string | null = null;
  public errorUsers: string | null = null;

  // ─── Données brutes ─────────────────────────────────────────────────────
  private structures: StructureProgrammatiqueResponse[] = [];
  private activites: ActivtieResponse[] = [];
  private usersCount: number | null = null;
  private typeInterventionMap: Map<string, string> = new Map();

  // ─── Données dérivées, affichées ────────────────────────────────────────
  public budgetCategories: BudgetCategory[] = [];
  public activeProjects: ProjetActif[] = [];
  public recentActivitesData: ActivtieResponse[] = [];

  public recentActivitesColumns: Column[] = [
    { label: 'Titre', key: 'titre', className: 'col-title', truncate: true, truncateLength: 60 },
    {
      label: 'Type',
      render: (row: ActivtieResponse) => this.getTypeInterventionLibelle(row.typeActivite),
    },
    {
      label: 'Début',
      render: (row: ActivtieResponse) => this.formatDate(row.dateDebut),
    },
    {
      label: 'Fin',
      render: (row: ActivtieResponse) => this.formatDate(row.dateFin),
    },
    {
      label: 'Statut',
      className: 'text-center',
      render: (row: ActivtieResponse) => this.renderStatutBadge(row.statut),
    },
  ];

  constructor(
    private authService: AuthService,
    private appService: AppService,
    private activiteService: ActiviteService,
    private structureProgrammatiqueService: StructureProgammatiqueService,
    private typeInterventionService: TypeInterventionService,
    private keycloakAdminService: KeycloakAdminService,
    private cd: ChangeDetectorRef,
  ) {
    this.username = this.authService.getUsername();
  }

  ngOnInit(): void {
    this.loadTypeInterventions();
    this.loadStructures();
    this.loadActivites();
    this.loadUsers();
  }

  // ─── Chargements indépendants (une source en erreur ne bloque pas les autres) ──

  private loadTypeInterventions(): void {
    this.typeInterventionService.getAllByAppId(this.appService.getAppId()).subscribe({
      next: (response: any) => {
        this.typeInterventionMap.clear();
        for (const ti of (response.data || []) as TypeInterventionResponse[]) {
          if (ti?.id) this.typeInterventionMap.set(ti.id, ti.libelle);
        }
        try { this.cd.markForCheck(); } catch { }
      },
      error: (e) => console.error('Erreur types intervention :', e),
    });
  }

  loadStructures(): void {
    this.isLoadingStructures = true;
    this.errorStructures = null;
    try { this.cd.markForCheck(); } catch { }

    this.structureProgrammatiqueService.getAllByAppId(this.appService.getAppId()).subscribe({
      next: (response: any) => {
        this.structures = response?.data ?? [];
        this.computeBudgetCategories();
        this.computeActiveProjects();
        this.isLoadingStructures = false;
        try { this.cd.markForCheck(); } catch { }
      },
      error: (e) => {
        console.error('Erreur chargement interventions :', e);
        this.errorStructures = "Impossible de charger les interventions.";
        this.isLoadingStructures = false;
        try { this.cd.markForCheck(); } catch { }
      },
    });
  }

  loadActivites(): void {
    this.isLoadingActivites = true;
    this.errorActivites = null;
    try { this.cd.markForCheck(); } catch { }

    this.activiteService.getAllByAppIdExcludeRegroupement(this.appService.getAppId()).subscribe({
      next: (response) => {
        this.activites = response?.data ?? [];
        this.recentActivitesData = [...this.activites]
          .sort((a, b) => new Date(b.dateCreation).getTime() - new Date(a.dateCreation).getTime())
          .slice(0, 6);
        this.isLoadingActivites = false;
        try { this.cd.markForCheck(); } catch { }
      },
      error: (e) => {
        console.error('Erreur chargement activités :', e);
        this.errorActivites = "Impossible de charger les activités récentes.";
        this.isLoadingActivites = false;
        try { this.cd.markForCheck(); } catch { }
      },
    });
  }

  loadUsers(): void {
    this.isLoadingUsers = true;
    this.errorUsers = null;
    try { this.cd.markForCheck(); } catch { }

    this.keycloakAdminService.listerUtilisateurs().subscribe({
      next: (users) => {
        this.usersCount = users?.length ?? 0;
        this.isLoadingUsers = false;
        try { this.cd.markForCheck(); } catch { }
      },
      error: (e) => {
        console.error('Erreur chargement utilisateurs :', e);
        this.errorUsers = "Indisponible";
        this.isLoadingUsers = false;
        try { this.cd.markForCheck(); } catch { }
      },
    });
  }

  // ─── KPIs (calculés à la volée à partir de l'état courant) ─────────────

  get kpis(): Kpi[] {
    const totalInterventions = this.flatten(this.structures).length;
    const totalActivites = this.activites.length;

    const totalAllocated = this.sumAcrossTree(this.structures, 'ENCAISSEMENT_ESTIMATIF');
    const totalSpent = this.sumAcrossTree(this.structures, 'DECAISSEMENT_ESTIMATIF');
    const tauxExecution = totalAllocated > 0
      ? Math.round((totalSpent / totalAllocated) * 100)
      : 0;

    return [
      {
        title: 'Interventions',
        value: this.isLoadingStructures ? '…' : String(totalInterventions),
        icon: 'account_tree',
        tone: 'primary',
      },
      {
        title: 'Activités',
        value: this.isLoadingActivites ? '…' : String(totalActivites),
        icon: 'task_alt',
        tone: 'success',
      },
      {
        title: 'Utilisateurs',
        value: this.isLoadingUsers ? '…' : (this.usersCount !== null ? String(this.usersCount) : '—'),
        icon: 'groups',
        tone: 'warning',
      },
      {
        title: "Taux d'exécution budgétaire",
        value: this.isLoadingStructures ? '…' : `${tauxExecution}%`,
        icon: 'trending_up',
        tone: tauxExecution > 100 ? 'danger' : 'primary',
      },
    ];
  }

  // ─── Calculs dérivés ─────────────────────────────────────────────────────

  private computeBudgetCategories(): void {
    const grouped = new Map<string, { spent: number; allocated: number }>();

    for (const node of this.flatten(this.structures)) {
      const label = this.getTypeInterventionLibelle(node.typeActivite);
      const spent = this.getCaracteristiqueValue(node, 'DECAISSEMENT_ESTIMATIF');
      const allocated = this.getCaracteristiqueValue(node, 'ENCAISSEMENT_ESTIMATIF');
      if (!spent && !allocated) continue;

      const current = grouped.get(label) ?? { spent: 0, allocated: 0 };
      current.spent += spent;
      current.allocated += allocated;
      grouped.set(label, current);
    }

    this.budgetCategories = Array.from(grouped.entries())
      .map(([name, v]) => ({ name, spent: v.spent, allocated: v.allocated }))
      .filter(c => c.allocated > 0 || c.spent > 0)
      .sort((a, b) => b.allocated - a.allocated);
  }

  private computeActiveProjects(): void {
    this.activeProjects = (this.structures || [])
      .filter(node => node.dateDebut && node.dateFin)
      .map(node => ({
        id: node.id,
        name: node.titre,
        dateDebut: node.dateDebut ?? null,
        dateFin: node.dateFin ?? null,
        progress: this.computeTimeProgress(node.dateDebut, node.dateFin),
        sousInterventionsCount: this.flatten(node.enfant || []).length,
      }))
      .sort((a, b) => b.progress - a.progress)
      .slice(0, 6);
  }

  private computeTimeProgress(dateDebut?: string | null, dateFin?: string | null): number {
    if (!dateDebut || !dateFin) return 0;
    const start = new Date(dateDebut).getTime();
    const end = new Date(dateFin).getTime();
    const now = Date.now();
    if (isNaN(start) || isNaN(end) || end <= start) return 0;
    const pct = ((now - start) / (end - start)) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  private flatten(nodes: StructureProgrammatiqueResponse[]): StructureProgrammatiqueResponse[] {
    const result: StructureProgrammatiqueResponse[] = [];
    const walk = (list: StructureProgrammatiqueResponse[]) => {
      for (const n of list || []) {
        result.push(n);
        if (n.enfant?.length) walk(n.enfant);
      }
    };
    walk(nodes);
    return result;
  }

  private sumAcrossTree(nodes: StructureProgrammatiqueResponse[], code: string): number {
    return this.flatten(nodes).reduce((acc, n) => acc + this.getCaracteristiqueValue(n, code), 0);
  }

  private getCaracteristiqueValue(node: StructureProgrammatiqueResponse, code: string): number {
    const v = node.caracteristiques?.find(c => c.code === code)?.valeur;
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  }

  getTypeInterventionLibelle(typeId: string | undefined): string {
    if (!typeId) return 'Non défini';
    return this.typeInterventionMap.get(typeId) || 'Type non trouvé';
  }

  formatAmount(value: number): string {
    try { return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(value); }
    catch { return String(value); }
  }

  formatDate(date?: string | null): string {
    if (!date) return '—';
    try { return new Intl.DateTimeFormat('fr-FR').format(new Date(date)); }
    catch { return String(date); }
  }

  private renderStatutBadge(statut: any): string {
    if (!statut) return '<span class="dash-badge dash-badge--muted">—</span>';
    const code = String(statut).toLowerCase();
    const map: Record<string, { cls: string; label: string }> = {
      termine: { cls: 'dash-badge--success', label: 'Terminé' },
      done: { cls: 'dash-badge--success', label: 'Terminé' },
      en_cours: { cls: 'dash-badge--warning', label: 'En cours' },
      in_progress: { cls: 'dash-badge--warning', label: 'En cours' },
      todo: { cls: 'dash-badge--muted', label: 'À faire' },
      en_attente: { cls: 'dash-badge--muted', label: 'En attente' },
    };
    const entry = map[code] ?? { cls: 'dash-badge--muted', label: String(statut) };
    return `<span class="dash-badge ${entry.cls}">${entry.label}</span>`;
  }

  budgetPercent(cat: BudgetCategory): number {
    if (!cat.allocated) return 0;
    return Math.min(100, Math.round((cat.spent / cat.allocated) * 100));
  }

  retryStructures(): void { this.loadStructures(); }
  retryActivites(): void { this.loadActivites(); }
  retryUsers(): void { this.loadUsers(); }
}